import { Alert, Box, Card, CardContent, Chip, Divider, Stack, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { registryApi } from '@/api/modules/registry';

/**
 * Public QR-scan landing. Anonymous (the backend permits unauthenticated GET
 * on /api/registry/passport/**). Renders the consumer-facing pack passport:
 * identity, manufacturer, descriptor, materials, carbon footprint, recent
 * lifecycle events.
 *
 * Mobile-first: single column, large headings, no horizontal tables.
 */
export function PassportPage() {
  const { qrToken } = useParams<{ qrToken: string }>();
  const query = useQuery({
    queryKey: ['registry', 'passport', qrToken],
    queryFn: () => registryApi.passport(qrToken!),
    enabled: Boolean(qrToken),
    retry: false
  });

  if (!qrToken) {
    return <Alert severity="warning">Missing QR token in URL.</Alert>;
  }
  if (query.isLoading) {
    return <Typography>Loading passport…</Typography>;
  }
  if (query.isError || !query.data) {
    return (
      <Alert severity="error">
        Passport not found for token <code>{qrToken}</code>. The pack may not be published.
      </Alert>
    );
  }

  const p = query.data;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 720, mx: 'auto' }}>
      <Typography variant="h1" gutterBottom>
        Battery passport
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        QR token: <code>{p.qrToken}</code> · Pack: {p.packSerialNumber}
      </Typography>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h3" gutterBottom>
            Manufacturer
          </Typography>
          <Typography>{p.manufacturer.legalName}</Typography>
          <Typography variant="body2" color="text.secondary">
            {p.manufacturer.code} · {p.manufacturer.jurisdiction}
          </Typography>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h3" gutterBottom>
            Descriptor
          </Typography>
          <Stack spacing={0.5}>
            <Typography>Chemistry: {p.descriptor.chemistry}</Typography>
            <Typography>Nominal voltage: {p.descriptor.nominalVoltage} V</Typography>
            <Typography>Capacity: {p.descriptor.capacityAh} Ah</Typography>
            <Typography>Energy: {p.descriptor.energyWh} Wh</Typography>
            <Typography>Weight: {p.descriptor.weightKg} kg</Typography>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h3" gutterBottom>
            Materials
          </Typography>
          <Stack spacing={1} divider={<Divider flexItem />}>
            {p.materials.map((m) => (
              <Stack key={m.materialCode} direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography>{m.materialName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {m.materialCode}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography>{m.percentage}%</Typography>
                  {m.recyclable && <Chip label="Recyclable" size="small" color="success" />}
                </Stack>
              </Stack>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h3" gutterBottom>
            Carbon footprint
          </Typography>
          <Typography>{p.carbonFootprint.totalCo2eKg} kg CO₂e</Typography>
          <Typography variant="body2" color="text.secondary">
            Methodology: {p.carbonFootprint.methodology}
          </Typography>
        </CardContent>
      </Card>

      {p.lifecycle.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h3" gutterBottom>
              Recent lifecycle
            </Typography>
            <Stack spacing={1} divider={<Divider flexItem />}>
              {p.lifecycle.map((event, idx) => (
                <Stack key={idx} direction="row" justifyContent="space-between">
                  <Typography>{event.type}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(event.occurredAt).toLocaleDateString()}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
        Generated {new Date(p.generatedAt).toLocaleString()}
      </Typography>
    </Box>
  );
}
